import { create } from 'zustand'
import type { Rental } from './rental'
import * as consts from './consts'
import { persist } from 'zustand/middleware';
import type { LocationSummary } from './consts';

export interface MapState {
    selectedLocation: string | null;  // which marker/location is selected (drives filtering + highlight)
    hoveredLocation: string | null;   // location currently being hovered in the list
    bounds: consts.Bounds | null;
    zoom: number | null;
    locations: Map<string, LocationSummary> | null;
}

export interface Store {
    rentals: Rental[];
    setRentals: (rentals: Rental[]) => void;
    appendRentals: (rentals: Rental[]) => void;
    mergeRentals: (rentals: Rental[]) => void;
    likedRentalIds: string[];
    setLikedRentalIds: (rentalIds: string[]) => void;
    showLikedOnly: boolean;
    setShowLikedOnly: (show: boolean) => void;
    toggleLike: (rental: Rental) => void;
    exchangeRates: Record<string, number>;
    setExchangeRates: (rates: Record<string, number>) => void;
    displayCurrency: string;
    setDisplayCurrency: (currency: string) => void;
    map: MapState;
    setMap: (update: Partial<MapState>) => void;
}

const useStore = create<Store, [["zustand/persist", Partial<Store>]]>(
    persist(
        (set) => ({
            rentals: [] as Rental[],
            setRentals: (rentals: Rental[]) => set({ rentals }),
            appendRentals: (rentals: Rental[]) => set((state) => {
                const existingIds = new Set(state.rentals.map(rental => rental.id));
                return {
                    rentals: [
                        ...state.rentals,
                        ...rentals.filter(rental => !existingIds.has(rental.id)),
                    ],
                };
            }),
            mergeRentals: (rentals: Rental[]) => set((state) => {
                const updates = new Map(rentals.map(rental => [rental.id, rental]));
                return {
                    rentals: state.rentals.map(rental => {
                        const update = updates.get(rental.id);
                        return update ? { ...rental, ...update } : rental;
                    }),
                };
            }),
            likedRentalIds: [],
            setLikedRentalIds: (rentalIds: string[]) => set({ likedRentalIds: rentalIds }),
            showLikedOnly: false,
            setShowLikedOnly: (show: boolean) => set({ showLikedOnly: show }),
            toggleLike: (rental: Rental) => set((state: Store) => {
                const prev = state.likedRentalIds;
                const isLiked = prev.some(id => id === rental.id);
                let updated: string[];

                if (isLiked) {
                    updated = prev.filter(id => id !== rental.id);
                } else {
                    updated = rental?.id ? [...prev, rental.id] : prev;
                }

                return { likedRentalIds: updated };
            }),
            exchangeRates: { [consts.BASE_CURRENCY]: 1 },
            setExchangeRates: (rates: Record<string, number>) => set({ exchangeRates: rates }),
            displayCurrency: consts.BASE_CURRENCY,
            setDisplayCurrency: (currency: string) => set({ displayCurrency: currency }),
            map: { selectedLocation: null, hoveredLocation: null, bounds: null, zoom: null, locations: null },
            setMap: (update: Partial<MapState>) => set((state) => ({ map: { ...state.map, ...update } })),
        }),
        {
            name: 'RentalStore',
            version: 1,
            partialize: (state) => ({
                displayCurrency: state.displayCurrency,
                likedRentalIds: state.likedRentalIds,
            }),
        }
    ));
export default useStore;
